import { useState, useMemo } from 'react';
import { Trash2, ArrowUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { formatDate } from '@/lib/utils';
import { SENSOR_STATUS_LABELS } from '@/features/quotes/components/sensors/SensorSelectDialog';
import type { QuoteSensor } from '@/features/quotes/types';

interface InvoiceSensorsTableProps {
  sensors: QuoteSensor[];
  isLoading?: boolean;
  onRemoveSensor: (sensorId: string) => void;
  onRemoveAllSensors: () => void;
  isRemoving?: boolean;
  disabled?: boolean;
}

/** Access nested object values by dot-separated key for sorting */
function getNestedValue(obj: Record<string, unknown>, path: string): string {
  const parts = path.split('.');
  let current: unknown = obj;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return '';
    current = (current as Record<string, unknown>)[part];
  }
  return String(current ?? '');
}

export function InvoiceSensorsTable({
  sensors,
  isLoading,
  onRemoveSensor,
  onRemoveAllSensors,
  isRemoving,
  disabled,
}: InvoiceSensorsTableProps) {
  const [filterQuery, setFilterQuery] = useState('');
  const [sortKey, setSortKey] = useState('name');
  const [sortDirection, setSortDirection] = useState<1 | -1>(1);

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDirection((d) => (d === 1 ? -1 : 1));
    } else {
      setSortKey(key);
      setSortDirection(1);
    }
  };

  const filteredAndSorted = useMemo(() => {
    let result = sensors;

    if (filterQuery) {
      const q = filterQuery.toLowerCase();
      result = result.filter(
        (s) =>
          s.name?.toLowerCase().includes(q) ||
          s.customid?.toLowerCase().includes(q) ||
          s.location?.name?.toLowerCase().includes(q) ||
          s.stream?.name?.toLowerCase().includes(q),
      );
    }

    return [...result].sort((a, b) => {
      const aVal = getNestedValue(a as unknown as Record<string, unknown>, sortKey);
      const bVal = getNestedValue(b as unknown as Record<string, unknown>, sortKey);
      if (aVal < bVal) return -1 * sortDirection;
      if (aVal > bVal) return 1 * sortDirection;
      return 0;
    });
  }, [sensors, filterQuery, sortKey, sortDirection]);

  const SortHeader = ({ label, field }: { label: string; field: string }) => (
    <th
      className="text-left px-2 py-2 font-medium text-xs cursor-pointer select-none"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {label}
        {sortKey === field && <ArrowUpDown className="h-3 w-3" />}
      </div>
    </th>
  );

  if (isLoading) {
    return (
      <p className="text-sm text-muted-foreground py-4 text-center">Loading sensors...</p>
    );
  }

  if (sensors.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-4 text-center">
        No sensors linked to this invoice yet.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2">
        <Input
          placeholder="Filter sensors..."
          value={filterQuery}
          onChange={(e) => setFilterQuery(e.target.value)}
          className="h-8 text-sm max-w-xs"
          aria-label="Filter sensors"
        />
        {!disabled && sensors.length > 0 && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="text-destructive hover:text-destructive shrink-0"
            onClick={onRemoveAllSensors}
            disabled={isRemoving}
            aria-label="Remove all sensors"
          >
            <Trash2 className="h-3.5 w-3.5 mr-1" aria-hidden />
            Remove all
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <SortHeader label="Custom ID" field="customid" />
              <SortHeader label="Name" field="name" />
              <SortHeader label="Location" field="location.name" />
              <SortHeader label="Stream" field="stream.name" />
              <th className="text-left px-2 py-2 font-medium text-xs">Calibration cert</th>
              <th className="text-left px-2 py-2 font-medium text-xs">Installation cert</th>
              <th className="text-left px-2 py-2 font-medium text-xs">Status</th>
              {!disabled && <th className="px-2 py-2 w-10" aria-label="Actions" />}
            </tr>
          </thead>
          <tbody>
            {filteredAndSorted.map((sensor) => {
              const cal = sensor.stream?.calibration;
              const iq = sensor.installationqualification;

              return (
                <tr key={sensor._id} className="border-t">
                  <td className="px-2 py-1.5 text-xs">{sensor.customid || '—'}</td>
                  <td className="px-2 py-1.5 text-xs font-medium">{sensor.name}</td>
                  <td className="px-2 py-1.5 text-xs">{sensor.location?.name || '—'}</td>
                  <td className="px-2 py-1.5 text-xs">{sensor.stream?.name || '—'}</td>
                  <td className="px-2 py-1.5 text-xs">
                    {cal?.cert_number ? (
                      <div>
                        <a
                          href={`/app#/calibrationrecords?id=${cal.calibrationrecord}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          {cal.cert_number}
                        </a>
                        {cal.date && (
                          <div className="text-muted-foreground">{formatDate(cal.date)}</div>
                        )}
                      </div>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-xs">
                    {iq?.report_number ? (
                      <div>
                        <a
                          href={`/app#/installationqualifications?id=${iq._id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          {iq.report_number}
                        </a>
                        {iq.date && (
                          <div className="text-muted-foreground">{formatDate(iq.date)}</div>
                        )}
                      </div>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-xs">
                    {sensor.status != null
                      ? (() => {
                          const statusInfo = SENSOR_STATUS_LABELS[sensor.status];
                          return (
                            <Badge variant={statusInfo?.variant ?? 'outline'} className="text-[10px]">
                              {statusInfo?.label ?? sensor.status}
                            </Badge>
                          );
                        })()
                      : '—'}
                  </td>
                  {!disabled && (
                    <td className="px-2 py-1.5">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-muted-foreground hover:text-destructive"
                        onClick={() => onRemoveSensor(sensor._id)}
                        disabled={isRemoving}
                        title={`Remove ${sensor.name}`}
                        aria-label={`Remove sensor ${sensor.name}`}
                      >
                        <Trash2 className="h-3 w-3" aria-hidden />
                      </Button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
